import {
  type AudioCaptureOptions,
  ConnectionState,
  type LocalAudioTrack,
  type LocalTrackPublication,
  type LocalVideoTrack,
  type ParticipantTrackPermission,
  type RemoteParticipant,
  type RemoteTrack,
  type RemoteTrackPublication,
  Room,
  RoomEvent,
  Track,
  createLocalAudioTrack,
  createLocalVideoTrack,
} from 'livekit-client'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { API_URL, VERSION_OVERRIDE } from '../lib/api'
import type { JoinHandoff } from '../lib/join-store'
import {
  RT_TOPIC,
  type RecAction,
  type RtMsg,
  type RtRole,
  type StatusMsg,
  decodeRtMsg,
  encodeRtMsg,
  parseParticipantMetadata,
} from '../lib/rt-protocol'
import { SAMPLE_RATE, TC_START_SAMPLES, samplesToTcFileTag } from '../lib/timecode'
import { getAnalyzerSummary, getOutputDeviceLabel } from './analyzer-bus'
import {
  type RecorderState,
  type RtRecorderBridge,
  type StorageMode,
  type TakeUpload,
  createRecorderBridge,
} from './recorder-bridge'
import type { RecorderDiag } from './use-analyzer'

export type RtConnection = 'mock' | 'connecting' | 'connected' | 'reconnecting' | 'disconnected' | 'failed'

export interface RtParticipantInfo {
  identity: string
  role: RtRole | 'unknown'
  name: string
  isLocal: boolean
  status: StatusMsg | null
  statusAtMs: number | null
}

export type CameraQuality = '720p' | '1080p'

/** Program-feed source (spec v2.5 视频源选择): screen capture, a camera /
 *  capture-card device published AS the program track, or nothing. */
export type ProgramSource = 'none' | 'screen' | 'camera'

/** Per-remote WebRTC receive stats (3 s sampler over inbound-rtp audio). */
export interface RtRemoteStats {
  jitterMs: number | null
  bitrateKbps: number | null
}

export interface UseRtRoomResult {
  connection: RtConnection
  error: string | null
  participants: RtParticipantInfo[]
  /** latest timecode (samples since midnight @48k) */
  tcSamples: number
  tcPlaying: boolean
  recState: RecorderState
  recTake: number
  /** program feed for remote viewers: studio screen-share, else studio camera */
  studioVideoTrack: RemoteTrack | null
  /** studio screen-share track only (null while not sharing) */
  programTrack: RemoteTrack | null
  /** local mic as a MediaStream (level meter) */
  localMicStream: MediaStream | null
  // actor recording state (S4)
  takes: TakeUpload[]
  storageMode: StorageMode
  /** actor: pick a save directory (user gesture); falls back to OPFS */
  chooseSaveFolder: () => Promise<StorageMode>
  // studio controls
  tcPlay: () => void
  tcStop: () => void
  sendRec: (action: RecAction, take: number) => void
  leave: () => void

  // ------------------------------------------------------------ UI v2 (U2)
  /** LiveKit room (null in mock mode) — chat + advanced controls. */
  room: Room | null
  localIdentity: string
  /** one-line event feed for the SLP bottom status bar */
  lastEvent: string
  // mic mute (studio "Mute Input", director/engineer talkback)
  micMuted: boolean
  setMicMuted: (muted: boolean) => void
  // Zoom camera grid
  cameraEnabled: boolean
  setCameraOn: (on: boolean) => void
  cameraQuality: CameraQuality
  setCameraQuality: (q: CameraQuality) => void
  localCameraTrack: LocalVideoTrack | null
  /** identity → remote camera track */
  remoteCameras: Record<string, RemoteTrack>
  /** identity → remote mic MediaStream (per-tile VU meters) */
  remoteAudioStreams: Record<string, MediaStream>
  // program share (studio)
  programSharing: boolean
  localProgramTrack: LocalVideoTrack | null
  toggleProgramShare: () => Promise<void>
  // devices / bitrate
  switchDevice: (kind: MediaDeviceKind, deviceId: string) => Promise<void>
  audioBitrateKbps: number
  setAudioBitrateKbps: (kbps: number) => void
  // wav prefix (cfg broadcast — studio sets, actor consumes)
  wavPrefix: string
  setWavPrefix: (prefix: string) => void
  // MTC / Video matrix (studio)
  tcDeny: string[]
  setTcSend: (identity: string, send: boolean) => void
  videoDeny: string[]
  setVideoSend: (identity: string, send: boolean) => void
  // soft disconnect (no server removeParticipant from a join token — UI-level)
  disconnectParticipant: (identity: string) => void

  // ------------------------------------------------------------ G2 (v2.10)
  /** identity → Connection slot (1..3) — the patch-bay wiring table */
  patchTable: Record<string, number>
  /** this end is patched (guests; studio is always operational) */
  selfPatched: boolean
  /** studio: wire a pool guest into a Connection slot (broadcast {t:'patch'}) */
  patchUser: (identity: string, slot: number) => void
  /** studio: return a patched guest to the waiting pool */
  unpatchUser: (identity: string) => void
  /** identity → receive stats (jitter/bitrate) */
  remoteStats: Record<string, RtRemoteStats>

  // ------------------------------------------------------------ UI v2 (F2)
  /** bumps whenever a {t:'files'} notification arrives — workspace refetch key */
  filesVersion: number
  /** broadcast {t:'files'} after an upload (also bumps filesVersion locally) */
  announceFiles: () => void
  /** active program-feed source (v2.5 Source selector) */
  programSource: ProgramSource
  /** switch the program feed: unpublish old tracks → publish new (seamless) */
  setProgramSource: (
    src: ProgramSource,
    opts?: { videoDeviceId?: string; audioDeviceId?: string },
  ) => Promise<void>

  // ------------------------------------------------------------ UI v2 (F3)
  /** live bytes written by the local recorder (actor; analyzer row 13) */
  recBytes: number
  /** worklet-dropout + disk-commit diagnostics for the analyzer (actor) */
  getRecorderDiag: () => RecorderDiag | null
  /** v2.6 Adjust Send Level — GainNode on the REALTIME stream only; the
   *  local BWF capture chain is a separate getUserMedia and is never touched */
  sendLevelPct: number
  setSendLevelPct: (pct: number) => void
  /** v2.6 AEC toggle — rebuilds the published mic capture (default off for
   *  actors; local BWF capture stays DSP-free regardless) */
  aecEnabled: boolean
  setAec: (on: boolean) => void
  /** v2.7 #2 — dim local monitoring −15 dB while PTT/talkback is open */
  speakerDim: boolean
  setSpeakerDim: (on: boolean) => void
  /** v2.7 #1 — space-bar push-to-talk currently open */
  pttActive: boolean
  /** v2.7 #2 — talkback hold (monitor ends): unmute + duck local monitoring */
  setTalkback: (on: boolean) => void
  /** v2.7 #6 — studio sets the ACTOR uplink tier (cfg broadcast) */
  setActorBitrateKbps: (kbps: number) => void
  /** v2.7 #7 — stereo tiers applied to the program-feed audio sender */
  setProgramAudioBitrateKbps: (kbps: number) => void
  /** last selected audiooutput deviceId ('' = system default) */
  audioOutputId: string
}

const PRO_AUDIO_CAPTURE = {
  // Professional recording chain: no browser DSP on the actor mic.
  echoCancellation: false,
  noiseSuppression: false,
  autoGainControl: false,
  channelCount: 1,
  sampleRate: 48000,
}

const CAMERA_CAPTURE: Record<CameraQuality, { width: number; height: number; frameRate: number }> = {
  '720p': { width: 1280, height: 720, frameRate: 30 },
  '1080p': { width: 1920, height: 1080, frameRate: 30 },
}

/** −15 dB ≈ ×0.178 on the monitor playback chain (v2.7 #2 speaker dimming). */
const DUCK_VOL = 0.1778

/** Director/engineer/studio talkback uplink — fixed 64 kbit/s mono (v2.7 #6). */
const TALKBACK_KBPS = 64

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n)
}

function sanitizeName(name: string): string {
  return name.replace(/[^A-Za-z0-9]+/g, '') || 'Guest'
}

/** v2.10 — short connect chime when this guest is patched ON AIR. */
function playPatchTone() {
  try {
    const Ctor =
      window.AudioContext ??
      (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!Ctor) return
    const ctx = new Ctor()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()
    osc.type = 'sine'
    osc.frequency.value = 880
    gain.gain.setValueAtTime(0.12, ctx.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.45)
    osc.connect(gain)
    gain.connect(ctx.destination)
    osc.start()
    osc.stop(ctx.currentTime + 0.5)
    osc.onended = () => void ctx.close().catch(() => undefined)
  } catch {
    /* no audio output — the visual ON AIR flip is the primary signal */
  }
}

/**
 * Owns the LiveKit Room lifecycle + the 'rt' data-channel protocol:
 *   studio → tc broadcast (250ms while playing, per-end gated by the MTC
 *            matrix via destinationIdentities) + rec commands + cfg
 *   all    → status heartbeat every 5s
 *   actor  → mic published with pro capture settings; rec commands drive the
 *            RtRecorderBridge.
 */
export function useRtRoom(handoff: JoinHandoff): UseRtRoomResult {
  const { join } = handoff
  const role = join.role
  const isMock = join.mock === true

  const [connection, setConnection] = useState<RtConnection>(isMock ? 'mock' : 'connecting')
  const [error, setError] = useState<string | null>(null)
  const [participants, setParticipants] = useState<RtParticipantInfo[]>([])
  const [tcSamples, setTcSamples] = useState(TC_START_SAMPLES)
  const [tcPlaying, setTcPlaying] = useState(false)
  const [recState, setRecState] = useState<RecorderState>('idle')
  const [recTake, setRecTake] = useState(1)
  const [localMicStream, setLocalMicStream] = useState<MediaStream | null>(null)

  const [takes, setTakes] = useState<TakeUpload[]>([])
  const [storageMode, setStorageMode] = useState<StorageMode>('unset')

  // UI v2 state
  const [lastEvent, setLastEvent] = useState('Connecting…')
  const [micMuted, setMicMutedState] = useState(false)
  const [cameraEnabled, setCameraEnabled] = useState(false)
  const [cameraQuality, setCameraQualityState] = useState<CameraQuality>('720p')
  const [localCameraTrack, setLocalCameraTrack] = useState<LocalVideoTrack | null>(null)
  const [remoteCameras, setRemoteCameras] = useState<Record<string, RemoteTrack>>({})
  const [remoteAudioStreams, setRemoteAudioStreams] = useState<Record<string, MediaStream>>({})
  const [programTrack, setProgramTrack] = useState<RemoteTrack | null>(null)
  const [studioCameraTrack, setStudioCameraTrack] = useState<RemoteTrack | null>(null)
  const [programSharing, setProgramSharing] = useState(false)
  const [localProgramTrack, setLocalProgramTrack] = useState<LocalVideoTrack | null>(null)
  const [audioBitrateKbps, setAudioBitrateKbpsState] = useState(128)
  const [wavPrefix, setWavPrefixState] = useState('')
  const [tcDeny, setTcDenyState] = useState<string[]>([])
  const [videoDeny, setVideoDenyState] = useState<string[]>([])
  const [remoteStats, setRemoteStats] = useState<Record<string, RtRemoteStats>>({})
  const [localIdentity, setLocalIdentity] = useState('local-mock')
  const [filesVersion, setFilesVersion] = useState(0)
  const [programSource, setProgramSourceState] = useState<ProgramSource>('none')

  // G2 v2.10 patch-bay — the studio's table survives a refresh (localStorage)
  // and is re-broadcast on connect + on every (re)join, so guests recover.
  const [patchTable, setPatchTableState] = useState<Record<string, number>>(() => {
    if (role !== 'studio') return {}
    try {
      const raw = localStorage.getItem(`rt.patch.${join.session.id}`)
      const obj = raw ? (JSON.parse(raw) as Record<string, number>) : {}
      return obj && typeof obj === 'object' ? obj : {}
    } catch {
      return {}
    }
  })
  const [selfPatched, setSelfPatchedState] = useState(false)

  // UI v2 F3 state
  const [recBytes, setRecBytesState] = useState(0)
  const [sendLevelPct, setSendLevelPctState] = useState(100)
  // non-actor mics are published with browser defaults (EC on); actor = off
  const [aecEnabled, setAecEnabledState] = useState(role !== 'actor')
  const [speakerDim, setSpeakerDimState] = useState(true)
  const [pttActive, setPttActive] = useState(false)
  const [audioOutputId, setAudioOutputId] = useState('')

  const displayName = handoff.displayName || join.display_name || role

  const roomRef = useRef<Room | null>(null)
  // Real BWF recorder bridge — actor role only (other roles never record).
  const bridgeRef = useRef<RtRecorderBridge | null>(null)
  if (!bridgeRef.current && role === 'actor') {
    bridgeRef.current = createRecorderBridge({
      linkToken: handoff.linkToken,
      sessionId: join.session.id,
      projectCode: join.session.project_code,
      actorName: displayName,
      frameRateFps: join.session.frame_rate_fps,
      mock: isMock,
    })
  }

  const statusMapRef = useRef(new Map<string, { msg: StatusMsg; atMs: number }>())
  const tcRef = useRef({ base: TC_START_SAMPLES, startedAtMs: 0, playing: false })
  const lastRemoteTcAtRef = useRef(0)
  const tcSamplesRef = useRef(TC_START_SAMPLES)
  const recTakeRef = useRef(1)
  const wavPrefixRef = useRef('')
  const tcDenyRef = useRef(new Set<string>())
  const videoDenyRef = useRef(new Set<string>())
  const hiddenRef = useRef(new Set<string>())
  const cameraQualityRef = useRef<CameraQuality>('720p')
  const audioBitrateRef = useRef(128)
  const statsPrevBytesRef = useRef(new Map<string, number>())
  // G2 v2.10 refs (handlers live outside React's render cycle)
  const patchTableRef = useRef<Record<string, number>>(patchTable)
  const selfPatchedRef = useRef(false)
  // v2.5 program source: which mode the NEXT ScreenShare-source publication
  // belongs to ('screen' via getDisplayMedia, 'camera' via capture device).
  const programModeRef = useRef<Exclude<ProgramSource, 'none'>>('screen')
  const programCamTrackRef = useRef<LocalVideoTrack | null>(null)
  const programAudioTrackRef = useRef<LocalAudioTrack | null>(null)

  // ---------------------------------------------------------------- F3 refs
  const micMutedRef = useRef(false)
  const aecRef = useRef(role !== 'actor')
  const sendLevelRef = useRef(100)
  const sendChainRef = useRef<{ ctx: AudioContext; gain: GainNode } | null>(null)
  /** remote-audio <audio> elements (monitor chain) — ducking targets */
  const audioElsRef = useRef<HTMLMediaElement[]>([])
  const duckTargetRef = useRef(1)
  const speakerDimRef = useRef(true)
  const pttRef = useRef(false)
  const talkbackRef = useRef(false)
  /** studio-chosen actor uplink tier (cfg broadcast payload) */
  const audioCfgKbpsRef = useRef<number | null>(null)
  /** studio-chosen program-feed audio tier (stereo 192/320/510) */
  const programAudioKbpsRef = useRef<number | null>(null)

  /** Ramp all attached remote-audio elements to the duck target over ~50 ms. */
  const rampMonitorVolume = useCallback(
    (target: number) => {
      duckTargetRef.current = target
      const els = audioElsRef.current.filter((el) => el.isConnected)
      if (els.length === 0) return
      const startVols = els.map((el) => el.volume)
      const t0 = performance.now()
      const step = () => {
        if (duckTargetRef.current !== target) return // superseded
        const k = Math.min(1, (performance.now() - t0) / 50)
        els.forEach((el, i) => {
          el.volume = (startVols[i] ?? 1) + (target - (startVols[i] ?? 1)) * k
        })
        if (k < 1) requestAnimationFrame(step)
      }
      requestAnimationFrame(step)
    },
    [],
  )

  /** Re-evaluate ducking from the current PTT/talkback/dim state. */
  const refreshDuck = useCallback(() => {
    const active = pttRef.current || talkbackRef.current
    rampMonitorVolume(active && speakerDimRef.current ? DUCK_VOL : 1)
  }, [rampMonitorVolume])

  /** Set maxBitrate on the local mic RTCRtpSender (no state side-effects). */
  const applySenderBitrate = useCallback((kbps: number) => {
    const sender = roomRef.current?.localParticipant.getTrackPublication(Track.Source.Microphone)
      ?.track?.sender
    if (!sender) return
    try {
      const params = sender.getParameters()
      if (!params.encodings || params.encodings.length === 0) {
        params.encodings = [{} as RTCRtpEncodingParameters]
      }
      const enc = params.encodings[0]
      if (enc) enc.maxBitrate = kbps * 1000
      void sender.setParameters(params).catch(() => undefined)
    } catch {
      /* sender not negotiated yet */
    }
  }, [])

  /** v2.7 #7 — maxBitrate on the program-feed audio sender (stereo tiers). */
  const applyProgramAudioBitrate = useCallback(() => {
    const kbps = programAudioKbpsRef.current
    if (!kbps) return
    const sender = roomRef.current?.localParticipant.getTrackPublication(
      Track.Source.ScreenShareAudio,
    )?.track?.sender
    if (!sender) return
    try {
      const params = sender.getParameters()
      if (!params.encodings || params.encodings.length === 0) {
        params.encodings = [{} as RTCRtpEncodingParameters]
      }
      const enc = params.encodings[0]
      if (enc) enc.maxBitrate = kbps * 1000
      void sender.setParameters(params).catch(() => undefined)
    } catch {
      /* sender not negotiated yet */
    }
  }, [])

  /**
   * v2.6 Adjust Send Level — wire mic → GainNode → MediaStreamDestination and
   * replaceTrack the published sender with the processed track. This sits on
   * the REALTIME branch only: the BWF bridge owns its own getUserMedia capture
   * (the fork point), so local recordings keep the original gain. Lazy: no
   * chain exists until the slider first leaves 100 %.
   */
  const ensureSendChain = useCallback(async () => {
    const track = roomRef.current?.localParticipant.getTrackPublication(Track.Source.Microphone)
      ?.track
    const sender = track?.sender
    const mst = track?.mediaStreamTrack
    if (!sender || !mst) return
    const old = sendChainRef.current
    sendChainRef.current = null
    if (old) void old.ctx.close().catch(() => undefined)
    try {
      const ctx = new AudioContext({ sampleRate: 48000 })
      if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined)
      const src = ctx.createMediaStreamSource(new MediaStream([mst]))
      const gain = ctx.createGain()
      gain.gain.value = sendLevelRef.current / 100
      const dest = ctx.createMediaStreamDestination()
      src.connect(gain)
      gain.connect(dest)
      const processed = dest.stream.getAudioTracks()[0]
      if (!processed) {
        void ctx.close().catch(() => undefined)
        return
      }
      await sender.replaceTrack(processed)
      sendChainRef.current = { ctx, gain }
    } catch {
      /* chain failed — sender keeps the raw mic track */
    }
  }, [])

  /** Re-attach the gain chain after the mic track was replaced (device
   *  switch / AEC rebuild) — only when the user has actually engaged it. */
  const reapplySendChain = useCallback(async () => {
    if (sendChainRef.current || sendLevelRef.current !== 100) await ensureSendChain()
  }, [ensureSendChain])

  const publish = useCallback(
    (msg: RtMsg, reliable: boolean, destinationIdentities?: string[]) => {
      const room = roomRef.current
      if (!room || room.state !== ConnectionState.Connected) return
      void room.localParticipant
        .publishData(encodeRtMsg(msg), {
          reliable,
          topic: RT_TOPIC,
          ...(destinationIdentities && destinationIdentities.length > 0 ? { destinationIdentities } : {}),
        })
        .catch(() => undefined)
    },
    [],
  )

  const syncParticipants = useCallback(() => {
    const room = roomRef.current
    const list: RtParticipantInfo[] = []
    const pushOne = (identity: string, metaRaw: string | undefined, isLocal: boolean) => {
      if (!isLocal && hiddenRef.current.has(identity)) return
      const meta = parseParticipantMetadata(metaRaw)
      const st = statusMapRef.current.get(identity)
      list.push({
        identity,
        role: meta?.role ?? 'unknown',
        name: meta?.name ?? identity,
        isLocal,
        status: st?.msg ?? null,
        statusAtMs: st?.atMs ?? null,
      })
    }
    if (room) {
      pushOne(room.localParticipant.identity || 'local', room.localParticipant.metadata, true)
      for (const p of room.remoteParticipants.values()) pushOne(p.identity, p.metadata, false)
    } else {
      // mock: just self
      const identity = 'local-mock'
      const st = statusMapRef.current.get(identity)
      list.push({
        identity,
        role,
        name: displayName,
        isLocal: true,
        status: st?.msg ?? null,
        statusAtMs: st?.atMs ?? null,
      })
    }
    // studio first, then director/engineer/actor, locals before remotes within a role
    const order: Record<string, number> = { studio: 0, director: 1, engineer: 2, actor: 3, unknown: 4 }
    list.sort((a, b) => (order[a.role] ?? 4) - (order[b.role] ?? 4))
    setParticipants(list)
  }, [displayName, role])

  /**
   * Studio: enforce the "Send Video" matrix column. The program (screen
   * share) track is gated per subscriber with server-side track permissions —
   * denied ends keep mic/camera/data but never receive the program track.
   */
  const applyVideoPermissions = useCallback(() => {
    const room = roomRef.current
    if (!room || role !== 'studio') return
    const denied = videoDenyRef.current
    if (denied.size === 0) {
      room.localParticipant.setTrackSubscriptionPermissions(true, [])
      return
    }
    const nonProgramSids: string[] = []
    for (const pub of room.localParticipant.trackPublications.values()) {
      const p = pub as LocalTrackPublication
      if (p.source !== Track.Source.ScreenShare && p.source !== Track.Source.ScreenShareAudio && p.trackSid) {
        nonProgramSids.push(p.trackSid)
      }
    }
    const perms: ParticipantTrackPermission[] = []
    for (const p of room.remoteParticipants.values()) {
      perms.push(
        denied.has(p.identity)
          ? { participantIdentity: p.identity, allowedTrackSids: nonProgramSids }
          : { participantIdentity: p.identity, allowAll: true },
      )
    }
    room.localParticipant.setTrackSubscriptionPermissions(false, perms)
  }, [role])

  // ------------------------------------------------------------ G2 (v2.10)

  /**
   * Guest side of the patch-bay: while UNPATCHED (waiting pool) the local mic
   * track must not be subscribable by anyone — same server-side permission
   * API the studio uses for the program matrix. Camera/data stay open so the
   * Zoom grid and chat keep working for pool guests. Patched → allow all.
   */
  const applyPatchSubscription = useCallback(() => {
    const room = roomRef.current
    if (!room || role === 'studio') return
    const lp = room.localParticipant
    if (selfPatchedRef.current) {
      lp.setTrackSubscriptionPermissions(true, [])
      return
    }
    const nonMicSids: string[] = []
    for (const pub of lp.trackPublications.values()) {
      const p = pub as LocalTrackPublication
      if (p.source !== Track.Source.Microphone && p.trackSid) nonMicSids.push(p.trackSid)
    }
    const perms: ParticipantTrackPermission[] = []
    for (const p of room.remoteParticipants.values()) {
      perms.push({ participantIdentity: p.identity, allowedTrackSids: nonMicSids })
    }
    lp.setTrackSubscriptionPermissions(false, perms)
  }, [role])

  const setPatchTable = useCallback((table: Record<string, number>) => {
    patchTableRef.current = table
    setPatchTableState(table)
  }, [])

  const persistPatchTable = useCallback(
    (table: Record<string, number>) => {
      if (role !== 'studio') return
      try {
        localStorage.setItem(`rt.patch.${join.session.id}`, JSON.stringify(table))
      } catch {
        /* storage full/blocked — in-memory table still drives the session */
      }
    },
    [join.session.id, role],
  )

  /** Studio: replay the whole patch table (connect + every (re)join). */
  const broadcastPatchTable = useCallback(() => {
    if (role !== 'studio') return
    for (const [identity, slot] of Object.entries(patchTableRef.current)) {
      publish({ t: 'patch', identity, slot, wallMs: Date.now() }, true)
    }
  }, [publish, role])

  /** Local reaction when THIS guest is (un)patched: chime + mute flip + perms. */
  const applySelfPatch = useCallback(
    (patched: boolean, slot: number | null) => {
      if (patched === selfPatchedRef.current) return
      selfPatchedRef.current = patched
      setSelfPatchedState(patched)
      const pub = roomRef.current?.localParticipant.getTrackPublication(Track.Source.Microphone)
      if (patched) {
        playPatchTone()
        // actors go live; director/engineer keep talkback semantics (muted at
        // rest, PTT/hold to speak) — patching only opens their receive path.
        if (role === 'actor') {
          void pub?.unmute().catch(() => undefined)
          setMicMutedState(false)
          micMutedRef.current = false
        }
        setLastEvent(`Connected — Remote User ${slot ?? '?'} (ON AIR)`)
      } else {
        if (role === 'actor') {
          void pub?.mute().catch(() => undefined)
          setMicMutedState(true)
          micMutedRef.current = true
        }
        setLastEvent('Returned to the waiting pool (STANDBY)')
      }
      applyPatchSubscription()
    },
    [applyPatchSubscription, role],
  )

  const handleRtMsg = useCallback(
    (msg: RtMsg, fromIdentity: string) => {
      if (msg.t === 'tc') {
        lastRemoteTcAtRef.current = performance.now()
        tcSamplesRef.current = msg.samples
        setTcSamples(msg.samples)
        setTcPlaying(true)
        bridgeRef.current?.noteTc(msg.samples, msg.wallMs)
        return
      }
      if (msg.t === 'status') {
        statusMapRef.current.set(fromIdentity, { msg, atMs: Date.now() })
        syncParticipants()
        return
      }
      if (msg.t === 'cfg') {
        wavPrefixRef.current = msg.wavPrefix
        setWavPrefixState(msg.wavPrefix)
        // v2.7 #6 — studio-controlled actor uplink tier. Only the ACTOR
        // applies it to its sender; director/engineer stay fixed at 64k mono.
        if (typeof msg.audioKbps === 'number' && msg.audioKbps > 0) {
          audioBitrateRef.current = msg.audioKbps
          setAudioBitrateKbpsState(msg.audioKbps)
          if (role === 'actor') applySenderBitrate(msg.audioKbps)
        }
        return
      }
      if (msg.t === 'files') {
        // workspace files changed upstream — bump so useSessionFiles refetches
        setFilesVersion((v) => v + 1)
        return
      }
      if (msg.t === 'patch') {
        // v2.10 — studio (re)wires a guest. Everyone mirrors the table (the
        // Connection slots render on all ends); the named guest also flips
        // its own mute/subscription state.
        const table = { ...patchTableRef.current }
        if (msg.slot == null) {
          delete table[msg.identity]
        } else {
          for (const [id, s] of Object.entries(table)) {
            if (s === msg.slot && id !== msg.identity) delete table[id]
          }
          table[msg.identity] = msg.slot
        }
        setPatchTable(table)
        const selfId = roomRef.current?.localParticipant.identity
        if (selfId && msg.identity === selfId) applySelfPatch(msg.slot != null, msg.slot)
        return
      }
      // rec command
      if (msg.t !== 'rec') return
      tcSamplesRef.current = msg.tcSamples
      setRecTake(msg.take)
      recTakeRef.current = msg.take
      if (msg.action === 'arm') setRecState('armed')
      else if (msg.action === 'start') setRecState('recording')
      else setRecState('idle')
      setLastEvent(
        msg.action === 'start'
          ? `Recording take ${msg.take} started`
          : msg.action === 'arm'
            ? `Armed for take ${msg.take}`
            : `Take ${msg.take} stopped`,
      )

      if (role === 'actor') {
        const bridge = bridgeRef.current
        if (!bridge) return
        const prefix = wavPrefixRef.current || join.session.project_code || 'RT'
        const fileBase = `${prefix}_${sanitizeName(displayName)}_T${pad2(msg.take)}_TC${samplesToTcFileTag(msg.tcSamples, join.session.frame_rate_fps)}`
        if (msg.action === 'arm') {
          void bridge.arm({ take: msg.take, tcSamples: msg.tcSamples, fileBase })
        } else if (msg.action === 'start') {
          // fileBase is re-derived from the START command's tcSamples (the
          // authoritative take timecode that also lands in bext TimeReference).
          void bridge.start({ take: msg.take, tcSamples: msg.tcSamples, fileBase })
        } else {
          void bridge.stop()
        }
      }
    },
    [applySelfPatch, applySenderBitrate, displayName, join.session.frame_rate_fps, join.session.project_code, role, setPatchTable, syncParticipants],
  )
  const handleRtMsgRef = useRef(handleRtMsg)
  handleRtMsgRef.current = handleRtMsg
  // ref-stable handles for room event handlers registered once per lifecycle
  const broadcastPatchTableRef = useRef(broadcastPatchTable)
  broadcastPatchTableRef.current = broadcastPatchTable
  const applyPatchSubscriptionRef = useRef(applyPatchSubscription)
  applyPatchSubscriptionRef.current = applyPatchSubscription

  // ------------------------------------------------ bridge → UI state (actor)
  useEffect(() => {
    const bridge = bridgeRef.current
    if (!bridge) return
    bridge.onTakesChanged = (t) => setTakes(t)
    bridge.onStorageModeChanged = (m) => setStorageMode(m)
    bridge.onStateChanged = (s) => setRecState(s)
    bridge.onBytes = (b) => setRecBytesState(b)
    setTakes(bridge.takes)
    setStorageMode(bridge.storageMode)
    return () => {
      bridge.onTakesChanged = undefined
      bridge.onStorageModeChanged = undefined
      bridge.onStateChanged = undefined
      bridge.onBytes = undefined
    }
  }, [])

  // ---------------------------------------------------------------- lifecycle
  useEffect(() => {
    let disposed = false
    audioElsRef.current = []
    const audioEls = audioElsRef.current

    const room = isMock ? null : new Room()
    roomRef.current = room
    // Dev-only debug handle for the e2e suite (real LiveKit mute/track state
    // assertions — never shipped: Vite strips this branch in prod builds).
    if (import.meta.env.DEV) {
      ;(window as unknown as { __rtRoom?: unknown }).__rtRoom = room
    }

    if (room) {
      room
        .on(RoomEvent.ConnectionStateChanged, (state: ConnectionState) => {
          if (disposed) return
          if (state === ConnectionState.Connected) {
            setConnection('connected')
            setLastEvent('Connection established')
            // v2.10: studio refresh recovery — replay the persisted patch table
            broadcastPatchTableRef.current()
          } else if (state === ConnectionState.Reconnecting) {
            setConnection('reconnecting')
            setLastEvent('Reconnecting…')
          } else if (state === ConnectionState.Disconnected) {
            setConnection('disconnected')
            setLastEvent('Disconnected')
          } else setConnection('connecting')
          syncParticipants()
        })
        .on(RoomEvent.ParticipantConnected, (p: RemoteParticipant) => {
          const meta = parseParticipantMetadata(p.metadata)
          setLastEvent(`Connection established to ${meta?.name ?? p.identity}`)
          syncParticipants()
          applyVideoPermissions()
          // v2.10: pool guests must (re)scope their mic permission to include
          // the newcomer; studio replays the patch table (rejoin recovery —
          // link identities are stable, so a crashed guest re-patches itself)
          applyPatchSubscriptionRef.current()
          broadcastPatchTableRef.current()
          // late joiner: re-broadcast the wav prefix + actor tier immediately
          if (role === 'studio' && (wavPrefixRef.current || audioCfgKbpsRef.current)) {
            publish(
              {
                t: 'cfg',
                wavPrefix: wavPrefixRef.current,
                wallMs: Date.now(),
                ...(audioCfgKbpsRef.current ? { audioKbps: audioCfgKbpsRef.current } : {}),
              },
              true,
            )
          }
        })
        .on(RoomEvent.ParticipantDisconnected, (p: RemoteParticipant) => {
          statusMapRef.current.delete(p.identity)
          const meta = parseParticipantMetadata(p.metadata)
          setLastEvent(`${meta?.name ?? p.identity} left the session`)
          setRemoteCameras((cur) => {
            if (!(p.identity in cur)) return cur
            const next = { ...cur }
            delete next[p.identity]
            return next
          })
          setRemoteAudioStreams((cur) => {
            if (!(p.identity in cur)) return cur
            const next = { ...cur }
            delete next[p.identity]
            return next
          })
          syncParticipants()
        })
        .on(RoomEvent.ParticipantMetadataChanged, () => syncParticipants())
        .on(RoomEvent.LocalTrackPublished, (pub: LocalTrackPublication) => {
          if (disposed) return
          if (pub.source === Track.Source.Camera) {
            setLocalCameraTrack((pub.track as LocalVideoTrack | undefined) ?? null)
            setCameraEnabled(true)
          } else if (pub.source === Track.Source.ScreenShare) {
            setLocalProgramTrack((pub.track as LocalVideoTrack | undefined) ?? null)
            setProgramSharing(true)
            setProgramSourceState(programModeRef.current)
            setLastEvent('Program share started')
          } else if (pub.source === Track.Source.ScreenShareAudio) {
            // v2.7 #7 — a chosen stereo tier follows every (re)publish
            applyProgramAudioBitrate()
          }
          applyVideoPermissions()
          // v2.10: a fresh trackSid changes the allowed-sids list (pool mode)
          applyPatchSubscriptionRef.current()
        })
        .on(RoomEvent.LocalTrackUnpublished, (pub: LocalTrackPublication) => {
          if (disposed) return
          if (pub.source === Track.Source.Camera) {
            setLocalCameraTrack(null)
            setCameraEnabled(false)
          } else if (pub.source === Track.Source.ScreenShare) {
            setLocalProgramTrack(null)
            setProgramSharing(false)
            // browser "Stop sharing" / source teardown — only fall back to
            // 'none' when no camera-program replacement is (being) published.
            if (!programCamTrackRef.current) setProgramSourceState('none')
            setLastEvent('Program share stopped')
          }
        })
        .on(
          RoomEvent.TrackSubscribed,
          (track: RemoteTrack, pub: RemoteTrackPublication, participant: RemoteParticipant) => {
            const meta = parseParticipantMetadata(participant.metadata)
            if (track.kind === Track.Kind.Audio) {
              const el = track.attach()
              el.setAttribute('data-rt-audio', participant.identity)
              el.volume = duckTargetRef.current // join mid-PTT → arrive ducked
              document.body.appendChild(el)
              audioEls.push(el)
              const mst = track.mediaStreamTrack
              if (mst && pub.source === Track.Source.Microphone) {
                setRemoteAudioStreams((cur) => ({ ...cur, [participant.identity]: new MediaStream([mst]) }))
              }
            } else if (track.kind === Track.Kind.Video) {
              if (pub.source === Track.Source.ScreenShare) {
                if (meta?.role === 'studio') setProgramTrack(track)
              } else {
                setRemoteCameras((cur) => ({ ...cur, [participant.identity]: track }))
                if (meta?.role === 'studio') setStudioCameraTrack(track)
              }
            }
          },
        )
        .on(
          RoomEvent.TrackUnsubscribed,
          (track: RemoteTrack, _pub: RemoteTrackPublication, participant: RemoteParticipant) => {
            for (const el of track.detach()) el.remove()
            setProgramTrack((cur) => (cur === track ? null : cur))
            setStudioCameraTrack((cur) => (cur === track ? null : cur))
            setRemoteCameras((cur) => {
              if (cur[participant.identity] !== track) return cur
              const next = { ...cur }
              delete next[participant.identity]
              return next
            })
            if (track.kind === Track.Kind.Audio) {
              setRemoteAudioStreams((cur) => {
                if (!(participant.identity in cur)) return cur
                const next = { ...cur }
                delete next[participant.identity]
                return next
              })
            }
          },
        )
        .on(RoomEvent.DataReceived, (payload: Uint8Array, participant?: RemoteParticipant, _kind?: unknown, topic?: string) => {
          if (topic !== RT_TOPIC) return
          const msg = decodeRtMsg(payload)
          if (msg) handleRtMsgRef.current(msg, participant?.identity ?? 'unknown')
        })

      void (async () => {
        try {
          await room.connect(join.livekit.url, join.livekit.token)
        } catch (err) {
          if (disposed) return
          setConnection('failed')
          setError(err instanceof Error ? err.message : 'Failed to connect to the session room')
          return
        }
        if (disposed) return
        setLocalIdentity(room.localParticipant.identity || 'local')
        // v2.10: pool guests lock down subscribability BEFORE any track is
        // published — no window where an unpatched mic could be subscribed.
        applyPatchSubscriptionRef.current()
        // Publish mic: actor with pro capture settings, everyone else default
        // talkback. A denied mic must NOT kill the room connection — surface a
        // soft warning and stay in the session (listen-only).
        try {
          if (role === 'actor') {
            await room.localParticipant.setMicrophoneEnabled(true, PRO_AUDIO_CAPTURE)
          } else {
            await room.localParticipant.setMicrophoneEnabled(true)
          }
          if (disposed) return
          const pub = room.localParticipant.getTrackPublication(Track.Source.Microphone)
          const mst = pub?.track?.mediaStreamTrack
          if (mst) setLocalMicStream(new MediaStream([mst]))
          // v2.7 #6 — non-actor uplinks are fixed talkback grade (64k mono);
          // the actor tier is studio-controlled via cfg.audioKbps.
          if (role !== 'actor') applySenderBitrate(TALKBACK_KBPS)
          // Talkback roles start muted — push-to-talk unmutes.
          if (role === 'director' || role === 'engineer') {
            await pub?.mute()
            if (!disposed) {
              setMicMutedState(true)
              micMutedRef.current = true
            }
          }
          // v2.10: actors enter the WAITING POOL — force-muted until the
          // studio patches them onto a Connection slot (a patch that raced
          // ahead of the mic publish wins: selfPatchedRef is already true).
          if (role === 'actor' && !selfPatchedRef.current) {
            await pub?.mute()
            if (!disposed) {
              setMicMutedState(true)
              micMutedRef.current = true
            }
          }
          // pool guests: scope mic subscribability now that tracks exist
          applyPatchSubscriptionRef.current()
        } catch {
          if (disposed) return
          setError(
            'Microphone unavailable — you are connected listen-only. Check browser mic permissions, then rejoin.',
          )
        }
        // Camera on by default (Zoom grid, 720p/30) — soft-fail without one.
        try {
          await room.localParticipant.setCameraEnabled(true, CAMERA_CAPTURE[cameraQualityRef.current])
        } catch {
          /* no camera / denied — grid just shows no local tile */
        }
        syncParticipants()
        applyVideoPermissions()
      })()
    } else {
      // Mock: render the full layout offline; mic for the local level meter only.
      syncParticipants()
      setLastEvent('Offline demo')
      if (role === 'actor' && navigator.mediaDevices?.getUserMedia) {
        navigator.mediaDevices
          .getUserMedia({ audio: PRO_AUDIO_CAPTURE })
          .then((stream) => {
            if (!disposed) setLocalMicStream(stream)
            else stream.getTracks().forEach((t) => t.stop())
          })
          .catch(() => undefined)
      }
    }

    // -------------------------------------------------- studio tc generator
    const tcTick = setInterval(() => {
      const tc = tcRef.current
      if (role === 'studio') {
        if (!tc.playing) return
        const samples = Math.round(tc.base + ((performance.now() - tc.startedAtMs) / 1000) * SAMPLE_RATE)
        tcSamplesRef.current = samples
        setTcSamples(samples)
        // MTC matrix: with denied ends, target only the allowed identities.
        const denied = tcDenyRef.current
        if (denied.size === 0) {
          publish({ t: 'tc', samples, wallMs: Date.now() }, false)
        } else {
          const r = roomRef.current
          const dest = r
            ? [...r.remoteParticipants.values()].map((p) => p.identity).filter((i) => !denied.has(i))
            : []
          if (dest.length > 0) publish({ t: 'tc', samples, wallMs: Date.now() }, false, dest)
        }
      } else {
        // freewheel detection: no tc msg for >700ms → stopped
        if (lastRemoteTcAtRef.current > 0 && performance.now() - lastRemoteTcAtRef.current > 700) {
          setTcPlaying(false)
        }
      }
    }, 250)

    // -------------------------------------------------- 5s status heartbeat
    const heartbeat = setInterval(() => {
      void (async () => {
        let net: StatusMsg['net']
        try {
          const t0 = performance.now()
          await fetch(`${API_URL}/health`, {
            cache: 'no-store',
            headers: VERSION_OVERRIDE
              ? { 'Cloudflare-Workers-Version-Overrides': `ohear-api="${VERSION_OVERRIDE}"` }
              : undefined,
          })
          net = { rttMs: Math.round(performance.now() - t0), lossPct: 0 }
          bridgeRef.current?.noteNetworkSample({ wallMs: Date.now(), rttMs: net.rttMs, lossPct: net.lossPct })
        } catch {
          net = undefined
        }
        let disk: StatusMsg['disk']
        if (role === 'actor' && navigator.storage?.estimate) {
          try {
            const est = await navigator.storage.estimate()
            const availMB = Math.round(((est.quota ?? 0) - (est.usage ?? 0)) / 1_048_576)
            disk = { availMB, needMB: Math.round(2 * 60 * 8.64 * 1.5) }
          } catch {
            disk = undefined
          }
        }
        const bridge = bridgeRef.current
        // F3: 8-item analyzer summary (analyzer bus) + device echo + preflight
        const sum = getAnalyzerSummary()
        const micLabel = roomRef.current?.localParticipant
          .getTrackPublication(Track.Source.Microphone)
          ?.track?.mediaStreamTrack?.label
        const msg: StatusMsg = {
          t: 'status',
          role,
          name: displayName,
          ready: true,
          // G1 v2.11 / G2 v2.10: in-room guests report 'pool' until the
          // studio patches them onto a Connection slot, then 'patched'.
          phase: role !== 'studio' && selfPatchedRef.current ? 'patched' : 'pool',
          ...(disk ? { disk } : {}),
          ...(net ? { net } : {}),
          ...(role === 'actor' && bridge
            ? { rec: { state: bridge.state, bytes: bridge.bytesWritten } }
            : {}),
          ...(sum ? { sum } : {}),
          dev: {
            ...(micLabel ? { inputLabel: micLabel.slice(0, 48) } : {}),
            outputLabel: (getOutputDeviceLabel() ?? 'System default').slice(0, 48),
            aec: aecRef.current,
          },
          ...(handoff.preflight ? { preflight: handoff.preflight } : {}),
          // G1 v2.9: the pre-flight medical report rides every heartbeat
          // (first one included) — studio Connection rows show it on hover.
          ...(handoff.grades ? { grades: handoff.grades } : {}),
        }
        publish(msg, true)
        // late-joiner safety: keep the wav prefix + actor tier flowing
        if (role === 'studio' && (wavPrefixRef.current || audioCfgKbpsRef.current)) {
          publish(
            {
              t: 'cfg',
              wavPrefix: wavPrefixRef.current,
              wallMs: Date.now(),
              ...(audioCfgKbpsRef.current ? { audioKbps: audioCfgKbpsRef.current } : {}),
            },
            true,
          )
        }
        // reflect own heartbeat in the local participant card too
        const selfId = roomRef.current?.localParticipant.identity || 'local-mock'
        statusMapRef.current.set(selfId, { msg, atMs: Date.now() })
        syncParticipants()
      })()
    }, 5000)

    // ------------------------------------- 3s receive-stats sampler (UI v2)
    const statsTick = setInterval(() => {
      const r = roomRef.current
      if (!r || r.state !== ConnectionState.Connected) return
      void (async () => {
        const next: Record<string, RtRemoteStats> = {}
        for (const p of r.remoteParticipants.values()) {
          const pub = p.getTrackPublication(Track.Source.Microphone)
          const track = pub?.track as RemoteTrack | undefined
          if (!track?.getRTCStatsReport) continue
          try {
            const report = await track.getRTCStatsReport()
            if (!report) continue
            for (const stat of report.values()) {
              if (stat.type === 'inbound-rtp') {
                const s = stat as { jitter?: number; bytesReceived?: number }
                const prev = statsPrevBytesRef.current.get(p.identity) ?? 0
                const bytes = s.bytesReceived ?? 0
                statsPrevBytesRef.current.set(p.identity, bytes)
                next[p.identity] = {
                  jitterMs: s.jitter != null ? Math.round(s.jitter * 1000 * 10) / 10 : null,
                  bitrateKbps: prev > 0 && bytes >= prev ? Math.round(((bytes - prev) * 8) / 3 / 1000) : null,
                }
                break
              }
            }
          } catch {
            /* stats unavailable mid-renegotiation — skip this tick */
          }
        }
        if (!disposed && Object.keys(next).length > 0) {
          setRemoteStats((cur) => ({ ...cur, ...next }))
        }
      })()
    }, 3000)

    return () => {
      disposed = true
      clearInterval(tcTick)
      clearInterval(heartbeat)
      clearInterval(statsTick)
      for (const el of audioEls) el.remove()
      const chain = sendChainRef.current
      sendChainRef.current = null
      if (chain) void chain.ctx.close().catch(() => undefined)
      setLocalMicStream((cur) => {
        cur?.getTracks().forEach((t) => t.stop())
        return null
      })
      if (room) void room.disconnect()
      roomRef.current = null
      if (import.meta.env.DEV) {
        ;(window as unknown as { __rtRoom?: unknown }).__rtRoom = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMock, join.livekit.token, join.livekit.url, role])

  // ---------------------------------------------------------- studio controls
  const tcPlay = useCallback(() => {
    const tc = tcRef.current
    if (tc.playing) return
    tc.startedAtMs = performance.now()
    tc.playing = true
    setTcPlaying(true)
  }, [])

  const tcStop = useCallback(() => {
    const tc = tcRef.current
    if (!tc.playing) return
    tc.base = Math.round(tc.base + ((performance.now() - tc.startedAtMs) / 1000) * SAMPLE_RATE)
    tc.playing = false
    tcSamplesRef.current = tc.base
    setTcSamples(tc.base)
    setTcPlaying(false)
  }, [])

  const sendRec = useCallback(
    (action: RecAction, take: number) => {
      const tcNow = tcSamplesRef.current
      // v2.10: rec commands roll PATCHED actors only — a pool actor must
      // never record. Monitor ends (director/engineer) always receive the
      // command (their REC overlays track state but they never record).
      const room = roomRef.current
      const dest = room
        ? [...room.remoteParticipants.values()]
            .filter((p) => {
              const meta = parseParticipantMetadata(p.metadata)
              return meta?.role !== 'actor' || patchTableRef.current[p.identity] != null
            })
            .map((p) => p.identity)
        : []
      if (!room || dest.length > 0) {
        publish({ t: 'rec', action, take, tcSamples: tcNow, wallMs: Date.now() }, true, dest)
      }
      setRecTake(take)
      recTakeRef.current = take
      if (action === 'arm') setRecState('armed')
      else if (action === 'start') setRecState('recording')
      else setRecState('idle')
      setLastEvent(
        action === 'start'
          ? `Recording take ${take} started`
          : action === 'arm'
            ? `Armed for take ${take}`
            : `Take ${take} stopped`,
      )
    },
    [publish],
  )

  const chooseSaveFolder = useCallback(async (): Promise<StorageMode> => {
    const bridge = bridgeRef.current
    if (!bridge) return 'unset'
    return bridge.chooseSaveFolder()
  }, [])

  const leave = useCallback(() => {
    bridgeRef.current?.dispose()
    bridgeRef.current = null
    const room = roomRef.current
    if (room) void room.disconnect()
    setConnection('disconnected')
  }, [])

  // ------------------------------------------------------------ UI v2 controls
  const setMicMuted = useCallback((muted: boolean) => {
    setMicMutedState(muted)
    micMutedRef.current = muted
    const room = roomRef.current
    const pub = room?.localParticipant.getTrackPublication(Track.Source.Microphone)
    if (!pub) return
    void (muted ? pub.mute() : pub.unmute()).catch(() => undefined)
  }, [])

  // -------------------------------------------------------- F3 audio controls

  /** v2.7 #1 — space bar push-to-talk while Mute Input is engaged. */
  useEffect(() => {
    const isTextTarget = (t: EventTarget | null): boolean => {
      const el = t as HTMLElement | null
      if (!el || !el.tagName) return false
      const tag = el.tagName
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable
    }
    const release = () => {
      if (!pttRef.current) return
      pttRef.current = false
      setPttActive(false)
      if (micMutedRef.current) {
        const pub = roomRef.current?.localParticipant.getTrackPublication(Track.Source.Microphone)
        void pub?.mute().catch(() => undefined)
      }
      refreshDuck()
    }
    const onDown = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || e.repeat) return
      if (isTextTarget(e.target)) return // typing in chat/filename — default space
      if (!micMutedRef.current || pttRef.current) return
      // v2.10: a pool actor is force-muted — PTT must not bypass the patch
      if (role === 'actor' && !selfPatchedRef.current) return
      e.preventDefault()
      pttRef.current = true
      setPttActive(true)
      const pub = roomRef.current?.localParticipant.getTrackPublication(Track.Source.Microphone)
      void pub?.unmute().catch(() => undefined)
      refreshDuck()
    }
    const onUp = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || !pttRef.current) return
      e.preventDefault()
      release()
    }
    const onBlur = () => release()
    window.addEventListener('keydown', onDown)
    window.addEventListener('keyup', onUp)
    window.addEventListener('blur', onBlur)
    return () => {
      window.removeEventListener('keydown', onDown)
      window.removeEventListener('keyup', onUp)
      window.removeEventListener('blur', onBlur)
      release()
    }
  }, [refreshDuck])

  /** v2.7 #2 — talkback hold (monitor ends): unmute + duck local monitoring. */
  const setTalkback = useCallback(
    (on: boolean) => {
      talkbackRef.current = on
      setMicMuted(!on)
      refreshDuck()
    },
    [refreshDuck, setMicMuted],
  )

  const setSpeakerDim = useCallback(
    (on: boolean) => {
      speakerDimRef.current = on
      setSpeakerDimState(on)
      refreshDuck() // mid-PTT toggle takes effect immediately
    },
    [refreshDuck],
  )

  /** v2.6 Adjust Send Level slider — realtime stream only (see ensureSendChain). */
  const setSendLevelPct = useCallback(
    (pct: number) => {
      const clamped = Math.max(0, Math.min(100, Math.round(pct)))
      sendLevelRef.current = clamped
      setSendLevelPctState(clamped)
      const chain = sendChainRef.current
      if (chain) {
        try {
          chain.gain.gain.setTargetAtTime(clamped / 100, chain.ctx.currentTime, 0.02)
        } catch {
          chain.gain.gain.value = clamped / 100
        }
      } else if (clamped !== 100) {
        void ensureSendChain()
      }
    },
    [ensureSendChain],
  )

  /** v2.6 AEC toggle — rebuild the PUBLISHED mic capture (BWF chain untouched). */
  const setAec = useCallback(
    (on: boolean) => {
      aecRef.current = on
      setAecEnabledState(on)
      const track = roomRef.current?.localParticipant.getTrackPublication(Track.Source.Microphone)
        ?.track as LocalAudioTrack | undefined
      if (!track) return
      const constraints: AudioCaptureOptions = {
        echoCancellation: on,
        // actors keep the rest of the DSP off no matter what; talkback roles
        // follow the toggle as a group
        noiseSuppression: role === 'actor' ? false : on,
        autoGainControl: role === 'actor' ? false : on,
        channelCount: 1,
        sampleRate: 48000,
      }
      void (async () => {
        try {
          await track.restartTrack(constraints)
          const mst = track.mediaStreamTrack
          if (mst) setLocalMicStream(new MediaStream([mst]))
          await reapplySendChain()
          setLastEvent(`Echo cancellation ${on ? 'enabled' : 'disabled'}`)
        } catch {
          /* capture rebuild failed — previous track keeps running */
        }
      })()
    },
    [reapplySendChain, role],
  )

  /** v2.7 #6 — studio picks the ACTOR uplink tier; broadcast via cfg. */
  const setActorBitrateKbps = useCallback(
    (kbps: number) => {
      audioCfgKbpsRef.current = kbps
      audioBitrateRef.current = kbps
      setAudioBitrateKbpsState(kbps)
      if (role === 'studio') {
        publish(
          {
            t: 'cfg',
            wavPrefix: wavPrefixRef.current,
            wallMs: Date.now(),
            audioKbps: kbps,
          },
          true,
        )
      }
    },
    [publish, role],
  )

  /** v2.7 #7 — stereo tier for the program-feed audio sender. */
  const setProgramAudioBitrateKbps = useCallback(
    (kbps: number) => {
      programAudioKbpsRef.current = kbps
      applyProgramAudioBitrate()
    },
    [applyProgramAudioBitrate],
  )

  const getRecorderDiag = useCallback((): RecorderDiag | null => {
    const b = bridgeRef.current
    return b ? { droppedFrames: b.droppedFrames, lastCommitMs: b.lastCommitMs } : null
  }, [])

  const setCameraOn = useCallback((on: boolean) => {
    setCameraEnabled(on)
    const room = roomRef.current
    if (!room) return
    void room.localParticipant
      .setCameraEnabled(on, on ? CAMERA_CAPTURE[cameraQualityRef.current] : undefined)
      .catch(() => setCameraEnabled(false))
  }, [])

  const setCameraQuality = useCallback((q: CameraQuality) => {
    cameraQualityRef.current = q
    setCameraQualityState(q)
    const room = roomRef.current
    if (!room) return
    const pub = room.localParticipant.getTrackPublication(Track.Source.Camera)
    if (pub?.track && 'restartTrack' in pub.track) {
      void (pub.track as LocalVideoTrack).restartTrack(CAMERA_CAPTURE[q]).catch(() => undefined)
    }
  }, [])

  const toggleProgramShare = useCallback(async () => {
    const room = roomRef.current
    if (!room) return
    const next = !programSharing
    try {
      // Program = screen/window capture WITH its audio (规格 §4.1: 音画同源).
      await room.localParticipant.setScreenShareEnabled(
        next,
        { audio: true },
        {
          screenShareEncoding: { maxBitrate: 4_000_000, maxFramerate: 30 },
          simulcast: false,
        },
      )
    } catch {
      /* user dismissed the picker — keep current state */
    }
  }, [programSharing])

  const switchDevice = useCallback(
    async (kind: MediaDeviceKind, deviceId: string) => {
      const room = roomRef.current
      if (!room || !deviceId) return
      try {
        await room.switchActiveDevice(kind, deviceId)
        if (kind === 'audioinput') {
          const pub = room.localParticipant.getTrackPublication(Track.Source.Microphone)
          const mst = pub?.track?.mediaStreamTrack
          if (mst) setLocalMicStream(new MediaStream([mst]))
          // device switch replaces the sender track — re-arm the gain chain
          await reapplySendChain()
        } else if (kind === 'audiooutput') {
          setAudioOutputId(deviceId)
        }
      } catch {
        /* device switch failed — keep the previous one */
      }
    },
    [reapplySendChain],
  )

  const setAudioBitrateKbps = useCallback((kbps: number) => {
    audioBitrateRef.current = kbps
    setAudioBitrateKbpsState(kbps)
    const room = roomRef.current
    const sender = room?.localParticipant.getTrackPublication(Track.Source.Microphone)?.track?.sender
    if (!sender) return
    try {
      const params = sender.getParameters()
      if (!params.encodings || params.encodings.length === 0) {
        params.encodings = [{} as RTCRtpEncodingParameters]
      }
      const enc = params.encodings[0]
      if (enc) enc.maxBitrate = kbps * 1000
      void sender.setParameters(params).catch(() => undefined)
    } catch {
      /* sender not negotiated yet */
    }
  }, [])

  const setWavPrefix = useCallback(
    (prefix: string) => {
      const clean = prefix.replace(/[^A-Za-z0-9_-]+/g, '').slice(0, 48)
      wavPrefixRef.current = clean
      setWavPrefixState(clean)
      if (role === 'studio') {
        publish({ t: 'cfg', wavPrefix: clean, wallMs: Date.now() }, true)
      }
    },
    [publish, role],
  )

  const setTcSend = useCallback((identity: string, send: boolean) => {
    const s = tcDenyRef.current
    if (send) s.delete(identity)
    else s.add(identity)
    setTcDenyState([...s])
  }, [])

  const setVideoSend = useCallback(
    (identity: string, send: boolean) => {
      const s = videoDenyRef.current
      if (send) s.delete(identity)
      else s.add(identity)
      setVideoDenyState([...s])
      applyVideoPermissions()
    },
    [applyVideoPermissions],
  )

  // ------------------------------------------------------------ UI v2 (F2)
  const announceFiles = useCallback(() => {
    // local bump → own workspace refetches; broadcast → everyone else does
    setFilesVersion((v) => v + 1)
    publish({ t: 'files', wallMs: Date.now() }, true)
  }, [publish])

  const setProgramSource = useCallback(
    async (src: ProgramSource, opts?: { videoDeviceId?: string; audioDeviceId?: string }) => {
      const room = roomRef.current
      if (src !== 'none') programModeRef.current = src
      if (!room) {
        setProgramSourceState(src)
        return
      }
      const lp = room.localParticipant

      // tear down any camera/capture-card program tracks (refs first so the
      // LocalTrackUnpublished handler can tell intent from browser teardown)
      const stopCamProgram = () => {
        const v = programCamTrackRef.current
        const a = programAudioTrackRef.current
        programCamTrackRef.current = null
        programAudioTrackRef.current = null
        try {
          if (v) lp.unpublishTrack(v, true)
        } catch {
          /* already gone */
        }
        try {
          if (a) lp.unpublishTrack(a, true)
        } catch {
          /* already gone */
        }
      }

      try {
        if (src === 'screen') {
          stopCamProgram()
          await lp.setScreenShareEnabled(
            true,
            { audio: true },
            { screenShareEncoding: { maxBitrate: 4_000_000, maxFramerate: 30 }, simulcast: false },
          )
          setProgramSourceState('screen')
          setLastEvent('Program source: screen share')
        } else if (src === 'camera') {
          // capture-card / camera published AS the ScreenShare-source track so
          // every remote end keeps treating it as the program feed.
          const vt = await createLocalVideoTrack({
            ...(opts?.videoDeviceId ? { deviceId: { exact: opts.videoDeviceId } } : {}),
            resolution: CAMERA_CAPTURE[cameraQualityRef.current],
          })
          stopCamProgram()
          programCamTrackRef.current = vt
          try {
            await lp.setScreenShareEnabled(false)
          } catch {
            /* not sharing */
          }
          await lp.publishTrack(vt, {
            source: Track.Source.ScreenShare,
            videoEncoding: { maxBitrate: 4_000_000, maxFramerate: 30 },
            simulcast: false,
          })
          if (opts?.audioDeviceId) {
            try {
              // program reference audio from the capture device — same track
              // group as the video (RTP-aligned), browser DSP off.
              const at = await createLocalAudioTrack({
                deviceId: { exact: opts.audioDeviceId },
                echoCancellation: false,
                noiseSuppression: false,
                autoGainControl: false,
              })
              await lp.publishTrack(at, { source: Track.Source.ScreenShareAudio })
              programAudioTrackRef.current = at
            } catch {
              /* program audio is optional — video-only program still works */
            }
          }
          setProgramSourceState('camera')
          setLastEvent('Program source: camera / capture device')
        } else {
          stopCamProgram()
          try {
            await lp.setScreenShareEnabled(false)
          } catch {
            /* not sharing */
          }
          setProgramSourceState('none')
          setLastEvent('Program source: none')
        }
      } catch {
        /* picker dismissed or device busy — keep the previous source */
      }
    },
    [],
  )

  const disconnectParticipant = useCallback(
    (identity: string) => {
      // No server-side removeParticipant from a join token — UI-level removal:
      // unsubscribe all of their tracks locally + drop them from the lists.
      hiddenRef.current.add(identity)
      const room = roomRef.current
      const p = room?.remoteParticipants.get(identity)
      if (p) {
        for (const pub of p.trackPublications.values()) {
          try {
            ;(pub as RemoteTrackPublication).setSubscribed(false)
          } catch {
            /* already unsubscribed */
          }
        }
      }
      setRemoteCameras((cur) => {
        if (!(identity in cur)) return cur
        const next = { ...cur }
        delete next[identity]
        return next
      })
      setRemoteAudioStreams((cur) => {
        if (!(identity in cur)) return cur
        const next = { ...cur }
        delete next[identity]
        return next
      })
      setLastEvent(`Disconnected ${identity} (local mute)`)
      syncParticipants()
    },
    [syncParticipants],
  )

  // ------------------------------------------------------------ G2 (v2.10)
  const patchUser = useCallback(
    (identity: string, slot: number) => {
      if (role !== 'studio' || slot < 1 || slot > 3) return
      const table = { ...patchTableRef.current }
      for (const [id, s] of Object.entries(table)) {
        if (s === slot && id !== identity) delete table[id] // slot is exclusive
      }
      table[identity] = slot
      setPatchTable(table)
      persistPatchTable(table)
      publish({ t: 'patch', identity, slot, wallMs: Date.now() }, true)
      setLastEvent(`${identity} connected to Remote User ${slot}`)
    },
    [persistPatchTable, publish, role, setPatchTable],
  )

  const unpatchUser = useCallback(
    (identity: string) => {
      if (role !== 'studio') return
      if (patchTableRef.current[identity] == null) return
      const table = { ...patchTableRef.current }
      delete table[identity]
      setPatchTable(table)
      persistPatchTable(table)
      publish({ t: 'patch', identity, slot: null, wallMs: Date.now() }, true)
      setLastEvent(`${identity} returned to the waiting pool`)
    },
    [persistPatchTable, publish, role, setPatchTable],
  )

  const studioVideoTrack = programTrack ?? studioCameraTrack

  return useMemo(
    () => ({
      connection,
      error,
      participants,
      tcSamples,
      tcPlaying,
      recState,
      recTake,
      studioVideoTrack,
      programTrack,
      localMicStream,
      takes,
      storageMode,
      chooseSaveFolder,
      tcPlay,
      tcStop,
      sendRec,
      leave,
      room: roomRef.current,
      localIdentity,
      lastEvent,
      micMuted,
      setMicMuted,
      cameraEnabled,
      setCameraOn,
      cameraQuality,
      setCameraQuality,
      localCameraTrack,
      remoteCameras,
      remoteAudioStreams,
      programSharing,
      localProgramTrack,
      toggleProgramShare,
      switchDevice,
      audioBitrateKbps,
      setAudioBitrateKbps,
      wavPrefix,
      setWavPrefix,
      tcDeny,
      setTcSend,
      videoDeny,
      setVideoSend,
      disconnectParticipant,
      remoteStats,
      filesVersion,
      announceFiles,
      programSource,
      setProgramSource,
      recBytes,
      getRecorderDiag,
      sendLevelPct,
      setSendLevelPct,
      aecEnabled,
      setAec,
      speakerDim,
      setSpeakerDim,
      pttActive,
      setTalkback,
      setActorBitrateKbps,
      setProgramAudioBitrateKbps,
      audioOutputId,
      patchTable,
      selfPatched,
      patchUser,
      unpatchUser,
    }),
    [
      connection,
      error,
      participants,
      tcSamples,
      tcPlaying,
      recState,
      recTake,
      studioVideoTrack,
      programTrack,
      localMicStream,
      takes,
      storageMode,
      chooseSaveFolder,
      tcPlay,
      tcStop,
      sendRec,
      leave,
      localIdentity,
      lastEvent,
      micMuted,
      setMicMuted,
      cameraEnabled,
      setCameraOn,
      cameraQuality,
      setCameraQuality,
      localCameraTrack,
      remoteCameras,
      remoteAudioStreams,
      programSharing,
      localProgramTrack,
      toggleProgramShare,
      switchDevice,
      audioBitrateKbps,
      setAudioBitrateKbps,
      wavPrefix,
      setWavPrefix,
      tcDeny,
      setTcSend,
      videoDeny,
      setVideoSend,
      disconnectParticipant,
      remoteStats,
      filesVersion,
      announceFiles,
      programSource,
      setProgramSource,
      recBytes,
      getRecorderDiag,
      sendLevelPct,
      setSendLevelPct,
      aecEnabled,
      setAec,
      speakerDim,
      setSpeakerDim,
      pttActive,
      setTalkback,
      setActorBitrateKbps,
      setProgramAudioBitrateKbps,
      audioOutputId,
      patchTable,
      selfPatched,
      patchUser,
      unpatchUser,
    ],
  )
}
